import { apiClient } from './api.js';
import { unwrap } from './api-error.js';
import type { ApiPaths } from '@reos/api-client';

/**
 * Klient prohlídek přes odkaz (krok CRM4, bod 1). Typy jsou generované
 * z docs/api/openapi.json; kolize s kalendářem, obsazenost i platnost
 * odkazu počítá server — UI je jen čte.
 */

type Json = 'application/json';

export type ViewingSlot = NonNullable<
  ApiPaths['/v1/crm/properties/{id}/viewing-slots']['get']['responses']['200']['content'][Json]
>['items'][number];
export type ViewingLink = NonNullable<
  ApiPaths['/v1/crm/properties/{id}/viewing-links']['get']['responses']['200']['content'][Json]
>['items'][number];
export type CreatedViewingLink = NonNullable<
  ApiPaths['/v1/crm/viewing-links']['post']['responses']['201']['content'][Json]
>;
export type CreateSlotsResult = NonNullable<
  ApiPaths['/v1/crm/viewing-slots']['post']['responses']['201']['content'][Json]
>;
export type ViewingBooking = NonNullable<
  ApiPaths['/v1/crm/viewing-bookings']['get']['responses']['200']['content'][Json]
>['items'][number];
export type MailPreview = NonNullable<
  ApiPaths['/v1/crm/viewing-links/{id}/invitation-preview']['post']['responses']['200']['content'][Json]
>;
export type PublicViewing = NonNullable<
  ApiPaths['/v1/public/viewings/{token}']['get']['responses']['200']['content'][Json]
>;
export type PublicBookingResult = NonNullable<
  ApiPaths['/v1/public/viewings/{token}/bookings']['post']['responses']['201']['content'][Json]
>;
export type CreateViewingLinkBody = ApiPaths['/v1/crm/viewing-links']['post']['requestBody']['content'][Json];

export async function listViewingSlots(propertyId: string): Promise<ViewingSlot[]> {
  const result = await unwrap(
    apiClient.GET('/v1/crm/properties/{id}/viewing-slots', { params: { path: { id: propertyId } } }),
  );
  return result.items;
}

export async function createViewingSlots(
  propertyId: string,
  slots: readonly { readonly startsAt: string; readonly endsAt: string }[],
): Promise<CreateSlotsResult> {
  return unwrap(apiClient.POST('/v1/crm/viewing-slots', { body: { propertyId, slots: [...slots] } }));
}

export async function withdrawViewingSlot(slotId: string): Promise<void> {
  await unwrap(apiClient.POST('/v1/crm/viewing-slots/{id}/withdraw', { params: { path: { id: slotId } } }));
}

export async function listViewingLinks(propertyId: string): Promise<ViewingLink[]> {
  const result = await unwrap(
    apiClient.GET('/v1/crm/properties/{id}/viewing-links', { params: { path: { id: propertyId } } }),
  );
  return result.items;
}

export async function createViewingLink(body: CreateViewingLinkBody): Promise<CreatedViewingLink> {
  return unwrap(apiClient.POST('/v1/crm/viewing-links', { body }));
}

export async function previewViewingInvitation(linkId: string, token: string): Promise<MailPreview> {
  return unwrap(
    apiClient.POST('/v1/crm/viewing-links/{id}/invitation-preview', { params: { path: { id: linkId } }, body: { token } }),
  );
}

/** Odeslání JEN po náhledu — `confirmed: true` je výslovné potvrzení makléře. */
export async function sendViewingInvitation(linkId: string, token: string): Promise<void> {
  await unwrap(
    apiClient.POST('/v1/crm/viewing-links/{id}/invitation', {
      params: { path: { id: linkId } },
      body: { token, confirmed: true },
    }),
  );
}

export interface ListBookingsParams {
  readonly propertyId?: string | undefined;
  readonly ownerUserId?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly status?: 'booked' | 'cancelled' | undefined;
}

export async function listViewingBookings(params: ListBookingsParams = {}): Promise<ViewingBooking[]> {
  const result = await unwrap(
    apiClient.GET('/v1/crm/viewing-bookings', {
      params: {
        query: {
          ...(params.propertyId !== undefined ? { propertyId: params.propertyId } : {}),
          ...(params.ownerUserId !== undefined ? { ownerUserId: params.ownerUserId } : {}),
          ...(params.from !== undefined ? { from: params.from } : {}),
          ...(params.to !== undefined ? { to: params.to } : {}),
          ...(params.status !== undefined ? { status: params.status } : {}),
        },
      },
    }),
  );
  return result.items;
}

export async function cancelViewingBooking(bookingId: string): Promise<void> {
  await unwrap(apiClient.POST('/v1/crm/viewing-bookings/{id}/cancel', { params: { path: { id: bookingId } } }));
}

// ------------------------------------------------ veřejná stránka (token)

export async function getPublicViewing(token: string): Promise<PublicViewing> {
  return unwrap(apiClient.GET('/v1/public/viewings/{token}', { params: { path: { token } } }));
}

export async function bookPublicViewing(
  token: string,
  body: {
    readonly slotId: string;
    readonly contactName: string;
    readonly contactEmail: string;
    readonly contactPhone?: string;
    readonly smsConsent?: boolean;
  },
): Promise<PublicBookingResult> {
  return unwrap(apiClient.POST('/v1/public/viewings/{token}/bookings', { params: { path: { token } }, body }));
}

export async function reschedulePublicViewing(token: string, slotId: string): Promise<PublicBookingResult> {
  return unwrap(apiClient.POST('/v1/public/viewings/{token}/reschedule', { params: { path: { token } }, body: { slotId } }));
}

export async function cancelPublicViewing(token: string): Promise<void> {
  await unwrap(apiClient.POST('/v1/public/viewings/{token}/cancel', { params: { path: { token } } }));
}

// ------------------------------------------- chatbot nabídky (krok AI8)

export type PropertyChatBody = ApiPaths['/v1/public/viewings/{token}/chat']['post']['requestBody']['content'][Json];
export type PropertyChatReply = NonNullable<
  ApiPaths['/v1/public/viewings/{token}/chat']['post']['responses']['200']['content'][Json]
>;

/**
 * Krok AI8: dotaz zájemce na nabídku. Fakta, představení jako AI i předání
 * makléři řeší server — stránka jen posílá zprávu a historii konverzace.
 */
export async function askPropertyChat(token: string, body: PropertyChatBody): Promise<PropertyChatReply> {
  return unwrap(apiClient.POST('/v1/public/viewings/{token}/chat', { params: { path: { token } }, body }));
}
